import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import type { TripWithItems, ItemType } from '@shared/types';
import { ITEM_TYPES, ITEM_TYPE_LABELS } from '@shared/types';

/** Chip labels stay short — the full heading sits right below in the page. */
const NAV_LABELS: Record<string, string> = {
  lodging: 'Stay',
  restaurant: 'Eat',
  activity: 'Do',
  tip: 'Tips',
};
import { GUEST_EMAIL_SUFFIX } from '@shared/types';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateRange } from '../lib/format';
import { ItemCard } from '../components/ItemCard';
import { AuthGate } from '../components/AuthGate';
import { ShareSheet } from '../components/ShareSheet';

export function TripPage() {
  const { slug = '' } = useParams();
  const [params] = useSearchParams();
  const token = params.get('k');

  const [trip, setTrip] = useState<TripWithItems | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [gate, setGate] = useState<{ headline: string; then: 'save' | 'clone' } | null>(null);
  const [toast, setToast] = useState<string | null>(
    params.get('new')
      ? 'Your trip page is ready'
      : params.get('saved')
        ? 'Saved to your wishlist'
        : null,
  );
  const [shareOpen, setShareOpen] = useState(false);
  // Shown to guests right after they save: the account is how the wishlist
  // survives beyond this browser's cookie.
  const [keepPrompt, setKeepPrompt] = useState(false);
  const { user } = useAuth();
  const isGuest = user?.email.endsWith(GUEST_EMAIL_SUFFIX) ?? false;

  const load = useCallback(async () => {
    try {
      setTrip(await api.getTripBySlug(slug, token));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load this trip.');
    }
  }, [slug, token]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  async function handleSave() {
    if (!trip) return;
    if (trip.viewer.hasSaved) {
      await api.unsaveTrip(trip.id);
      setTrip({ ...trip, viewer: { ...trip.viewer, hasSaved: false } });
      return;
    }
    const result = await api.saveTrip(trip.id, token);
    if (result.gated) {
      // Ungated in Phase 1: the server reports the gate without enforcing it,
      // so we only interrupt when it says the gate is live.
      if (result.enforced) {
        setGate({ headline: result.message, then: 'save' });
        return;
      }
      setToast('Sign in to keep this on your wishlist');
      return;
    }
    setTrip({ ...trip, viewer: { ...trip.viewer, hasSaved: true } });
    if (isGuest) {
      setKeepPrompt(true);
    } else {
      setToast('Saved to your wishlist');
    }
  }

  async function handleClone() {
    if (!trip) return;
    const result = await api.cloneTrip(trip.id, token);
    if (result.gated) {
      if (result.enforced) {
        setGate({ headline: result.message, then: 'clone' });
        return;
      }
      setToast('Sign in to start your own version');
      return;
    }
    window.location.href = `/trips/${result.id}/edit`;
  }

  if (error) {
    return (
      <main className="mx-auto max-w-2xl px-5 py-20 text-center">
        <h1 className="font-display text-title text-ink">This trip is private</h1>
        <p className="mt-3 font-body text-muted">{error}</p>
        <Link to="/" className="btn-quiet mt-6 inline-block">
          Go to your trips
        </Link>
      </main>
    );
  }

  if (!trip) {
    return (
      <main className="mx-auto max-w-2xl px-5 py-20">
        <div className="h-8 w-2/3 rounded bg-raised" />
        <div className="mt-3 h-4 w-1/3 rounded bg-raised" />
      </main>
    );
  }

  const dates = formatDateRange(trip.startDate, trip.endDate);
  const grouped = ITEM_TYPES.map((type) => ({
    type,
    items: trip.items.filter((i) => i.type === type),
  })).filter((g) => g.items.length > 0);

  return (
    <main className="pb-24">
      {trip.coverImage && (
        <div className="w-full">
          <img
            src={trip.coverImage}
            alt=""
            className="h-56 sm:h-80 w-full object-cover"
          />
        </div>
      )}

      <div className="mx-auto max-w-2xl px-5">
        <header className={trip.coverImage ? 'pt-7' : 'pt-12'}>
          <h1 className="font-display text-title sm:text-title-lg text-ink">{trip.title}</h1>

          <p className="mt-2 font-util text-meta text-muted">
            {[trip.destination, dates].filter(Boolean).join(' · ')}
          </p>

          <div className="mt-5 flex items-center gap-2.5">
            {trip.owner.avatarUrl ? (
              <img src={trip.owner.avatarUrl} alt="" className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-raised font-util text-meta text-muted">
                {(trip.owner.name ?? '?').slice(0, 1).toUpperCase()}
              </div>
            )}
            <span className="font-util text-meta text-muted">
              Shared by {trip.owner.name ?? 'a friend'}
            </span>
          </div>

          {trip.notes && <p className="note-aside mt-6">{trip.notes}</p>}
        </header>

        {/* Quick nav: one chip per section that actually exists. Sticky so a
            reader deep in "What we did" can jump back to "Where we ate". */}
        {grouped.length > 1 && (
          <nav
            aria-label="Sections"
            className="sticky top-0 z-30 -mx-5 mt-8 border-b border-ink/8 bg-surface/95 px-5 backdrop-blur-sm"
          >
            <div className="flex gap-1 overflow-x-auto py-2">
              {grouped.map(({ type }) => (
                <a
                  key={type}
                  href={`#${type}`}
                  className="shrink-0 whitespace-nowrap rounded-btn px-3 py-1.5 font-util
                             text-meta font-medium text-muted transition-colors
                             hover:bg-raised hover:text-ink"
                >
                  {NAV_LABELS[type] ?? type}
                </a>
              ))}
            </div>
          </nav>
        )}

        {grouped.length === 0 ? (
          <p className="mt-12 font-body text-muted">Nothing added to this trip yet.</p>
        ) : (
          grouped.map(({ type, items }) => (
            <section key={type} id={type} className="mt-10 scroll-mt-14">
              <h2 className="font-util text-section font-semibold text-ink/70">
                {ITEM_TYPE_LABELS[type as ItemType].plural}
              </h2>
              <div className="mt-1 divide-y divide-ink/8">
                {items.map((item) => (
                  <ItemCard key={item.id} item={item} />
                ))}
              </div>
            </section>
          ))
        )}
      </div>

      <p className="mx-auto max-w-2xl px-5 pt-14 text-center font-util text-meta text-muted">
        Made on{' '}
        <Link to="/" className="text-accent underline">
          Here&rsquo;s My Trip
        </Link>
        {' '}· Someone always asks. Answer once.
      </p>

      {/* One primary action, fixed to the thumb on mobile. */}
      <div className="fixed inset-x-0 bottom-0 border-t border-ink/10 bg-surface/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-2xl items-center gap-2 px-5 py-3">
          {trip.viewer.isOwner ? (
            <>
              <Link to={`/trips/${trip.id}/edit`} className="btn-quiet">
                Edit trip
              </Link>
              <button onClick={() => setShareOpen(true)} className="btn-primary ml-auto">
                Share this trip
              </button>
            </>
          ) : (
            <>
              <button onClick={handleClone} className="btn-quiet">
                Make one like this
              </button>
              <button onClick={handleSave} className="btn-primary ml-auto">
                {trip.viewer.hasSaved ? 'On your wishlist' : 'Save to my wishlist'}
              </button>
            </>
          )}
        </div>
      </div>

      {keepPrompt && (
        <div
          className="fixed inset-x-0 bottom-16 z-40 mx-auto w-full max-w-md px-4"
          role="status"
        >
          <div className="rounded-note bg-ink px-4 py-3 text-surface shadow-lg">
            <p className="font-body text-[0.9375rem]">
              On your wishlist. Create a free account and it follows you to any
              device.
            </p>
            <div className="mt-2 flex items-center gap-2">
              <Link
                to="/signup"
                className="rounded-btn bg-surface px-3 py-1.5 font-util text-meta font-semibold text-ink"
              >
                Create a free account
              </Link>
              <button
                onClick={() => setKeepPrompt(false)}
                className="px-2 py-1.5 font-util text-meta text-surface/70"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div
          role="status"
          className="fixed inset-x-0 bottom-20 z-40 mx-auto w-fit rounded-btn bg-ink px-4 py-2
                     font-util text-meta text-surface"
        >
          {toast}
        </div>
      )}

      {gate && (
        <AuthGate
          headline={gate.headline}
          onClose={() => setGate(null)}
          onAuthed={async () => {
            setGate(null);
            await load();
            setToast('Saved to your trips');
          }}
        />
      )}

      {shareOpen && trip.viewer.isOwner && (
        <ShareSheet trip={trip} onClose={() => setShareOpen(false)} />
      )}
    </main>
  );
}
